/**
 * Pure amortization schedule engine.
 *
 * Extracted from calculatorStore so the logic can be unit-tested without
 * React or Zustand. The store delegates to calculateSchedule() and then
 * applies manual one-time-payment (OTP) overrides on top.
 */

import { addMonths, addDays } from 'date-fns';
import type {
  MortgageTermsData,
  PaymentFrequency,
} from '../types/mortgageTypes';
import type { AnnualExpensesData, ExtraPaymentsData, AmortizationEntry } from '../store/calculatorStore';
import {
  calculateMonthlyRate,
  calculateMonthlyPayment,
  calculateMortgageInsurance,
  calculateConventionalPMI,
  calculateCMHCProvincialTax,
  getPaymentsPerYear,
  getPaymentForFrequency,
  isSubMonthlyFrequency,
} from './mortgageCalculations';
import { getCanadianRenewalCount, getEffectiveCanadianRenewalSegmentRates } from './canadianRenewalRates';
import { normalizeMortgageTerms } from './canadianMortgageType';
import {
  getMaxPaymentCount,
  getNominalPaymentCount,
} from './scheduleHorizon';

export interface ScheduleMeta {
  nominalPaymentCount: number;
  paidOff: boolean;
  extendedBeyondNominal: boolean;
  hitSafetyCap: boolean;
}

export interface ScheduleResult {
  schedule: AmortizationEntry[];
  meta: ScheduleMeta;
}

/**
 * Advance a payment date by one period of the given frequency.
 * - monthly: +1 calendar month (same day-of-month)
 * - bi-weekly / accelerated-bi-weekly: +14 days
 * - weekly / accelerated-weekly: +7 days
 * - semi-monthly: toggle between day 1 and day 15, advancing month when wrapping
 */
const advancePaymentDate = (date: Date, frequency: PaymentFrequency): Date => {
  if (frequency === 'bi-weekly' || frequency === 'accelerated-bi-weekly') {
    return addDays(date, 14);
  }
  if (frequency === 'weekly' || frequency === 'accelerated-weekly') {
    return addDays(date, 7);
  }
  if (frequency === 'semi-monthly') {
    const day = date.getDate();
    if (day < 15) {
      const next = new Date(date);
      next.setDate(15);
      return next;
    }
    // On or past the 15th -> jump to the 1st of next month
    const next = addMonths(date, 1);
    next.setDate(1);
    return next;
  }
  return addMonths(date, 1);
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const computeMonthlyExpenses = (
  expenses: AnnualExpensesData,
  homePrice: number
): number =>
  Object.values(expenses).reduce((sum, expense) => {
    if (expense.type === 'amount') {
      return sum + expense.value;
    }
    return sum + (homePrice * (expense.value / 100)) / 12;
  }, 0);

const monthKeyForDate = (date: Date): string => `${date.getFullYear()}-${date.getMonth()}`;

const advanceExtraPaymentDate = (
  date: Date,
  frequency: ExtraPaymentsData['frequency']
): Date => {
  if (frequency === 'weekly') return addDays(date, 7);
  if (frequency === 'bi-weekly') return addDays(date, 14);
  if (frequency === 'yearly') return addMonths(date, 12);
  if (frequency === 'semi-monthly') {
    if (date.getDate() < 15) {
      const next = new Date(date);
      next.setDate(15);
      return next;
    }
    const next = addMonths(date, 1);
    next.setDate(1);
    return next;
  }
  return addMonths(date, 1);
};

const firstExtraPaymentDate = (
  startDate: Date,
  frequency: ExtraPaymentsData['frequency']
): Date => {
  const first = new Date(startDate);
  if (frequency !== 'semi-monthly') return first;

  if (first.getDate() <= 1) {
    first.setDate(1);
    return first;
  }
  if (first.getDate() <= 15) {
    first.setDate(15);
    return first;
  }

  const next = addMonths(first, 1);
  next.setDate(1);
  return next;
};

const buildMonthlyExtraPaymentMap = (
  extraPayments: ExtraPaymentsData,
  scheduleEndDate: Date
): Map<string, number> => {
  const monthlyExtras = new Map<string, number>();
  if (!extraPayments?.amount || extraPayments.amount <= 0 || !extraPayments.startDate) {
    return monthlyExtras;
  }

  const endDate = extraPayments.endDate ? new Date(extraPayments.endDate) : scheduleEndDate;
  const effectiveEndDate = endDate < scheduleEndDate ? endDate : scheduleEndDate;
  let paymentDate = firstExtraPaymentDate(
    new Date(extraPayments.startDate),
    extraPayments.frequency
  );

  while (paymentDate <= effectiveEndDate) {
    const key = monthKeyForDate(paymentDate);
    monthlyExtras.set(key, (monthlyExtras.get(key) || 0) + extraPayments.amount);
    paymentDate = advanceExtraPaymentDate(paymentDate, extraPayments.frequency);
  }

  return monthlyExtras;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ScheduleInputs {
  mortgageTerms: MortgageTermsData;
  annualExpenses: AnnualExpensesData;
  extraPayments: ExtraPaymentsData;
}

export interface ManualOneTimePaymentEdit {
  idx: number;
  amount: number;
}

const normaliseManualEdits = (edits: ManualOneTimePaymentEdit[]): Map<number, number> => {
  const manualByIndex = new Map<number, number>();
  edits.forEach((edit) => {
    if (
      Number.isInteger(edit.idx) &&
      edit.idx >= 0 &&
      Number.isFinite(edit.amount) &&
      edit.amount > 0
    ) {
      manualByIndex.set(edit.idx, edit.amount);
    }
  });
  return manualByIndex;
};

/**
 * Recalculate a monthly-row amortization schedule after row-level one-time
 * payment edits. The regular engine handles recurring extras while this helper
 * handles user-edited lump sums from the amortization table.
 */
export function applyManualOneTimePaymentEdits(
  schedule: AmortizationEntry[],
  inputs: ScheduleInputs,
  edits: ManualOneTimePaymentEdit[]
): AmortizationEntry[] {
  const manualByIndex = normaliseManualEdits(edits);
  if (manualByIndex.size === 0) {
    return schedule.map((entry) => ({
      ...entry,
      oneTimePayment: entry.oneTimePayment || undefined,
      totalPayment: entry.payment + entry.expensePayment + (entry.oneTimePayment || 0),
    }));
  }

  const { mortgageTerms } = inputs;
  const {
    country,
    homePrice,
    amortizationPeriod,
    mortgageType,
    loanProgram,
    pmi,
    fhaDetails,
    usdaDetails,
    cmhcDetails,
  } = mortgageTerms;

  const result = schedule.map((entry, index) => ({
    ...entry,
    oneTimePayment: manualByIndex.get(index) ?? entry.oneTimePayment,
  }));

  const firstManualIndex = Math.min(...manualByIndex.keys());
  const priorBalance =
    firstManualIndex > 0
      ? result[firstManualIndex - 1].remainingBalance
      : schedule[firstManualIndex].remainingBalance +
        schedule[firstManualIndex].principalPayment +
        (schedule[firstManualIndex].oneTimePayment || 0);

  let runningBalance = priorBalance;

  for (let i = firstManualIndex; i < result.length; i++) {
    const currentEntry = result[i];
    const currentRate = currentEntry.currentRate ?? mortgageTerms.rate;
    const currentMonthlyRate = calculateMonthlyRate(currentRate, country, mortgageType);
    const interestDue = runningBalance * currentMonthlyRate;
    const insurancePayment = calculateMortgageInsurance(
      loanProgram,
      runningBalance,
      homePrice,
      amortizationPeriod,
      i + 1,
      fhaDetails,
      usdaDetails,
      cmhcDetails
    );
    const pmiPayment = calculateConventionalPMI(runningBalance, homePrice, pmi, loanProgram);
    const scheduledDebtPayment = Math.max(
      0,
      currentEntry.payment - (currentEntry.pmiPayment ?? 0)
    );
    const principalPayment = Math.max(
      0,
      Math.min(scheduledDebtPayment - insurancePayment - interestDue, runningBalance)
    );
    const manualPayment = manualByIndex.get(i) ?? currentEntry.oneTimePayment ?? 0;

    runningBalance = Math.max(0, runningBalance - principalPayment - manualPayment);

    const actualPayment = principalPayment + interestDue + insurancePayment + pmiPayment;
    result[i] = {
      ...currentEntry,
      payment: actualPayment,
      principalPayment,
      interestPayment: interestDue + insurancePayment,
      remainingBalance: runningBalance,
      oneTimePayment: manualPayment > 0 ? manualPayment : undefined,
      pmiPayment: pmiPayment > 0 ? pmiPayment : undefined,
      totalPayment: actualPayment + currentEntry.expensePayment + manualPayment,
    };

    if (runningBalance <= 0) {
      return result.slice(0, i + 1);
    }
  }

  return result;
}

/**
 * Calculate the full amortization schedule for a mortgage.
 * Returns schedule rows plus metadata about payoff vs entered amortization.
 */
export function calculateScheduleWithMeta(inputs: ScheduleInputs): ScheduleResult {
  const { annualExpenses, extraPayments } = inputs;
  const mortgageTerms = normalizeMortgageTerms(inputs.mortgageTerms);

  const {
    country,
    homePrice,
    downPaymentAmount,
    rate,
    amortizationPeriod,
    startDate,
    paymentFrequency,
    mortgageType,
    loanProgram,
    pmi,
    fhaDetails,
    vaDetails,
    usdaDetails,
    cmhcDetails,
    armDetails,
    variableDetails,
    canadianTermDetails,
  } = mortgageTerms;

  // ------------------------------------------------------------------
  // 1. Principal - add any rolled-in upfront fees
  // ------------------------------------------------------------------
  let principal = homePrice - downPaymentAmount;

  if (loanProgram === 'fha' && fhaDetails) {
    principal += fhaDetails.upfrontMIP;
  } else if (loanProgram === 'va' && vaDetails) {
    principal += principal * vaDetails.fundingFeeRate;
  } else if (loanProgram === 'usda' && usdaDetails) {
    principal += principal * usdaDetails.upfrontFee;
  } else if (loanProgram === 'cmhc' && cmhcDetails) {
    const baseLoan = homePrice - downPaymentAmount;
    const cmhcPremium = baseLoan * cmhcDetails.premiumRate;
    const provincialTax = cmhcDetails.includeProvincialTax
      ? calculateCMHCProvincialTax(cmhcPremium, cmhcDetails.province)
      : 0;
    principal += cmhcPremium + provincialTax;
  }

  // ------------------------------------------------------------------
  // 2. Derived payment values
  // ------------------------------------------------------------------
  const monthlyRate = calculateMonthlyRate(rate, country, mortgageType);
  const numberOfPayments = amortizationPeriod * 12;

  const baseMonthlyPayment = calculateMonthlyPayment(principal, monthlyRate, numberOfPayments);

  // For variable mortgages with rate forecasts, payments are recomputed when
  // the rate steps. This mutates inside the loop.
  let dynamicMonthlyPayment = baseMonthlyPayment;
  let lastVariableRate = rate;
  let lastArmRate = rate;

  const paymentsPerYear = getPaymentsPerYear(paymentFrequency);
  const nominalPayments = getNominalPaymentCount(amortizationPeriod, paymentFrequency);
  const maxPayments = getMaxPaymentCount(amortizationPeriod, paymentFrequency, mortgageTerms);
  const isSubMonthly = isSubMonthlyFrequency(paymentFrequency);

  const monthlyExpenses = computeMonthlyExpenses(annualExpenses, homePrice);

  // ------------------------------------------------------------------
  // 3. ARM rate pre-computation
  // ------------------------------------------------------------------
  const buildARMAdjustedRates = (): number[] => {
    if (!armDetails) return [];
    const fullyIndexedRate = armDetails.indexRate + armDetails.margin;
    const lifetimeMax = rate + armDetails.lifetimeCap;
    const lifetimeMin = Math.max(0, rate - armDetails.lifetimeCap);
    const totalAdjustments = Math.ceil(
      (amortizationPeriod * 12 - armDetails.initialFixedPeriod * 12) /
        armDetails.adjustmentFrequency
    );
    const adjustedRates: number[] = [];
    let prevRate = rate;

    for (let i = 0; i < Math.max(totalAdjustments, 1); i++) {
      const cap = i === 0 ? armDetails.initialAdjustmentCap : armDetails.periodicCap;
      const maxRate = prevRate + cap;
      const minRate = prevRate - cap;
      let nextRate = Math.min(Math.max(fullyIndexedRate, minRate), maxRate);
      nextRate = Math.min(Math.max(nextRate, lifetimeMin), lifetimeMax);
      adjustedRates.push(nextRate);
      prevRate = nextRate;
    }
    return adjustedRates;
  };

  const armAdjustedRates = buildARMAdjustedRates();

  // ------------------------------------------------------------------
  // 4. Per-payment rate lookup
  // ------------------------------------------------------------------
  // Map a payment index to "months since start" using the cadence so that ARM
  // and variable rate schedules remain month-indexed regardless of frequency.
  const paymentsToMonths = (paymentNumber: number): number =>
    (paymentNumber * 12) / paymentsPerYear;

  const getCurrentRate = (paymentNumber: number): number => {
    if (mortgageType === 'adjustable' && armDetails) {
      const monthsSinceStart = paymentsToMonths(paymentNumber);
      const initialFixedMonths = armDetails.initialFixedPeriod * 12;

      if (monthsSinceStart < initialFixedMonths) return rate;

      const monthsSinceFirstAdjustment = monthsSinceStart - initialFixedMonths;
      const adjustmentIndex = Math.floor(
        monthsSinceFirstAdjustment / armDetails.adjustmentFrequency
      );
      return armAdjustedRates[Math.min(adjustmentIndex, armAdjustedRates.length - 1)];
    }

    if (mortgageType === 'variable' && variableDetails) {
      const baseRate = variableDetails.primeRate - variableDetails.discount;
      const changes = variableDetails.rateChanges;
      if (!changes || changes.length === 0) return baseRate;

      const monthsVarRate = paymentsToMonths(paymentNumber);
      const yearOfPayment = Math.floor(monthsVarRate / 12) + 1;

      const cumulativeDelta = changes
        .filter((c) => yearOfPayment >= c.year)
        .reduce((sum, c) => sum + c.delta, 0);

      return baseRate + cumulativeDelta;
    }

    return rate;
  };

  // ------------------------------------------------------------------
  // 5. Generate payment date sequence
  // ------------------------------------------------------------------
  let currentDate = new Date(startDate);
  currentDate.setDate(1);

  const allPaymentDates: Date[] = [];
  let tempDate = new Date(currentDate);
  for (let i = 0; i < maxPayments; i++) {
    allPaymentDates.push(new Date(tempDate));
    tempDate = advancePaymentDate(tempDate, paymentFrequency);
  }

  // Build year-month -> payment count map for expense proration
  const paymentsPerMonth = new Map<string, number>();
  allPaymentDates.forEach((d) => {
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    paymentsPerMonth.set(key, (paymentsPerMonth.get(key) || 0) + 1);
  });

  // Reset date cursor
  currentDate = new Date(startDate);
  currentDate.setDate(1);

  // ------------------------------------------------------------------
  // 6. Extra-payment tracking
  // ------------------------------------------------------------------
  // The amortization table is monthly-bucketed, so recurring extras are first
  // generated at their own cadence and then aggregated into monthly totals.
  const monthlyExtraPayments = buildMonthlyExtraPaymentMap(
    extraPayments,
    allPaymentDates[allPaymentDates.length - 1] ?? currentDate
  );
  let lastAppliedExtraMonthKey = '';

  // ------------------------------------------------------------------
  // 7. Main amortization loop
  // ------------------------------------------------------------------
  let remainingBalance = principal;
  const schedule: AmortizationEntry[] = [];

  // For bi-weekly: accumulate into monthly buckets and flush one entry per month
  type MonthBucket = {
    year: number;
    month: number;
    payment: number;
    principalPayment: number;
    interestPayment: number;
    expensePayment: number;
    oneTimePayment: number;
    pmiPayment: number;
    remainingBalance: number;
    currentRate: number;
  };
  let bucket: MonthBucket | null = null;

  const flushBucket = () => {
    if (!bucket) return;
    const toFlush = bucket;
    bucket = null;
    const totalExtra = toFlush.oneTimePayment > 0 ? toFlush.oneTimePayment : undefined;
    schedule.push({
      date: new Date(toFlush.year, toFlush.month, 1),
      payment: toFlush.payment,
      principalPayment: toFlush.principalPayment,
      interestPayment: toFlush.interestPayment,
      expensePayment: toFlush.expensePayment,
      totalPayment: toFlush.payment + toFlush.expensePayment + toFlush.oneTimePayment,
      remainingBalance: toFlush.remainingBalance,
      oneTimePayment: totalExtra,
      pmiPayment: toFlush.pmiPayment > 0 ? toFlush.pmiPayment : undefined,
      currentRate: toFlush.currentRate,
    });
  };

  let paymentsExecuted = 0;

  for (let payment = 0; payment < maxPayments && remainingBalance > 0; payment++) {
    paymentsExecuted = payment + 1;
    const currentRate = getCurrentRate(payment);
    const currentMonthlyRate = calculateMonthlyRate(currentRate, country, mortgageType);

    const periodRate = currentMonthlyRate * (12 / paymentsPerYear);

    const beyondNominal = payment >= nominalPayments;

    // Recompute payment when adjustable-payment variable rates step. Canadian
    // VRMs keep the payment fixed and let amortization/balance absorb changes.
    if (
      mortgageType === 'variable' &&
      variableDetails?.variableType !== 'vrm' &&
      variableDetails?.rateChanges &&
      variableDetails.rateChanges.length > 0 &&
      currentRate !== lastVariableRate
    ) {
      const remainingPayments = nominalPayments - payment;
      if (remainingPayments > 0 && remainingBalance > 0) {
        const remainingMonths = Math.max(1, Math.round((remainingPayments * 12) / paymentsPerYear));
        dynamicMonthlyPayment = calculateMonthlyPayment(
          remainingBalance,
          currentMonthlyRate,
          remainingMonths
        );
      }
      lastVariableRate = currentRate;
    }

    // USA ARM: recalc P&I at each adjustment using remaining original amortization term.
    if (
      country === 'USA' &&
      mortgageType === 'adjustable' &&
      armDetails &&
      currentRate !== lastArmRate &&
      remainingBalance > 0
    ) {
      const monthsElapsed = paymentsToMonths(payment);
      const remainingMonths = Math.max(1, numberOfPayments - monthsElapsed);
      dynamicMonthlyPayment = calculateMonthlyPayment(
        remainingBalance,
        currentMonthlyRate,
        remainingMonths
      );
      lastArmRate = currentRate;
    }

    const paymentAmount = getPaymentForFrequency(dynamicMonthlyPayment, paymentFrequency);

    const interestDue = remainingBalance * periodRate;
    const isFixedPaymentVrm =
      country === 'Canada' &&
      mortgageType === 'variable' &&
      variableDetails?.variableType === 'vrm';
    const unpaidInterest = isFixedPaymentVrm
      ? Math.max(interestDue - paymentAmount, 0)
      : 0;
    const interestPayment = isFixedPaymentVrm
      ? Math.min(interestDue, paymentAmount)
      : interestDue;
    const principalPayment = unpaidInterest > 0
      ? 0
      : Math.max(0, Math.min(paymentAmount - interestDue, remainingBalance));

    const insuranceMonthIndex = Math.min(
      Math.floor(paymentsToMonths(payment)) + 1,
      numberOfPayments
    );
    const insurancePayment = calculateMortgageInsurance(
      loanProgram,
      remainingBalance,
      homePrice,
      amortizationPeriod,
      insuranceMonthIndex,
      fhaDetails,
      usdaDetails,
      cmhcDetails
    );

    const pmiAmount = calculateConventionalPMI(remainingBalance, homePrice, pmi, loanProgram);

    // Extra payments are monthly aggregates. For sub-monthly mortgages, apply
    // the month's aggregate on the first mortgage payment in that month so the
    // bucketed row reports the total extra paid in the month exactly once.
    let extraPayment = 0;
    const extraMonthKey = monthKeyForDate(currentDate);
    if (!isSubMonthly || extraMonthKey !== lastAppliedExtraMonthKey) {
      extraPayment = monthlyExtraPayments.get(extraMonthKey) || 0;
      lastAppliedExtraMonthKey = extraMonthKey;
    }

    remainingBalance = Math.max(
      0,
      remainingBalance + unpaidInterest - principalPayment - extraPayment
    );

    let expensePayment = monthlyExpenses;
    if (isSubMonthly) {
      const key = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;
      const paymentsInMonth = paymentsPerMonth.get(key) || 1;
      expensePayment = monthlyExpenses / paymentsInMonth;
    }

    // Use actual amount paid rather than scheduled amount - they differ on the
    // final payment when the remaining balance is less than the scheduled payment.
    const actualPayment = principalPayment + interestPayment + insurancePayment + pmiAmount;

    if (isSubMonthly) {
      const y = currentDate.getFullYear();
      const m = currentDate.getMonth();
      const isNewMonth = !bucket || bucket.year !== y || bucket.month !== m;
      if (isNewMonth && bucket) flushBucket();
      if (!bucket || isNewMonth) {
        bucket = {
          year: y,
          month: m,
          payment: 0,
          principalPayment: 0,
          interestPayment: 0,
          expensePayment: 0,
          oneTimePayment: 0,
          pmiPayment: 0,
          remainingBalance: 0,
          currentRate, // use first payment's rate in the month (for ARM boundary correctness)
        };
      }
      bucket.payment += actualPayment;
      bucket.principalPayment += principalPayment;
      bucket.interestPayment += interestPayment + insurancePayment;
      bucket.expensePayment += expensePayment;
      bucket.oneTimePayment += extraPayment;
      bucket.pmiPayment += pmiAmount;
      bucket.remainingBalance = remainingBalance;
      // Keep currentRate from first payment in month (so boundary month gets fixed rate, not adjusted)
    } else {
      schedule.push({
        date: new Date(currentDate),
        payment: actualPayment,
        principalPayment,
        interestPayment: interestPayment + insurancePayment,
        expensePayment,
        totalPayment: actualPayment + expensePayment + extraPayment,
        remainingBalance,
        oneTimePayment: extraPayment > 0 ? extraPayment : undefined,
        pmiPayment: pmiAmount > 0 ? pmiAmount : undefined,
        currentRate,
        beyondNominalAmortization: beyondNominal ? true : undefined,
      });
    }

    if (remainingBalance <= 0) break;

    currentDate = advancePaymentDate(currentDate, paymentFrequency);
  }

  if (bucket) flushBucket();

  // Tag monthly-bucketed rows that fall after the nominal amortization window.
  if (isSubMonthly) {
    const nominalMonthRows = amortizationPeriod * 12;
    schedule.forEach((entry, idx) => {
      if (idx >= nominalMonthRows) {
        entry.beyondNominalAmortization = true;
      }
    });
  }

  const lastBalance = schedule.length ? schedule[schedule.length - 1].remainingBalance : principal;
  const paidOff = lastBalance <= 0.01;
  const extendedBeyondNominal = paidOff && paymentsExecuted > nominalPayments;
  const hitSafetyCap = !paidOff && paymentsExecuted >= maxPayments;

  const meta: ScheduleMeta = {
    nominalPaymentCount: nominalPayments,
    paidOff,
    extendedBeyondNominal,
    hitSafetyCap,
  };

  // ------------------------------------------------------------------
  // 8. Canadian term renewal post-processing
  // ------------------------------------------------------------------
  if (country === 'Canada' && canadianTermDetails) {
    for (const entry of schedule) {
      if (entry.isRenewalDate) {
        delete entry.isRenewalDate;
      }
    }

    const termMonths = canadianTermDetails.termLength * 12;
    const renewalCount = getCanadianRenewalCount(amortizationPeriod, canadianTermDetails.termLength);

    for (let k = 1; k <= renewalCount; k++) {
      const startIdx = k * termMonths;
      if (startIdx < schedule.length) {
        schedule[startIdx].isRenewalDate = true;
      }
    }

    const effectiveSegmentRates = getEffectiveCanadianRenewalSegmentRates(
      rate,
      canadianTermDetails,
      amortizationPeriod
    );

    if (effectiveSegmentRates) {
      for (let k = 1; k <= renewalCount; k++) {
        const startIdx = k * termMonths;
        if (startIdx >= schedule.length) break;

        const segmentRate = effectiveSegmentRates[k - 1];
        const renewalMonthlyRate = calculateMonthlyRate(segmentRate, country, mortgageType);
        const endExclusive = Math.min(schedule.length, (k + 1) * termMonths);
        const balanceAtRenewal = schedule[startIdx - 1]?.remainingBalance ?? 0;
        const remainingMonthsFromRenewal = schedule.length - startIdx;

        if (balanceAtRenewal > 0 && remainingMonthsFromRenewal > 0) {
          const newPayment = calculateMonthlyPayment(
            balanceAtRenewal,
            renewalMonthlyRate,
            remainingMonthsFromRenewal
          );

          let runningBalance = balanceAtRenewal;
          for (let i = startIdx; i < endExclusive; i++) {
            if (runningBalance <= 0) break;
            const interestForPeriod = runningBalance * renewalMonthlyRate;
            const principalForPeriod = Math.min(
              newPayment - interestForPeriod,
              runningBalance
            );
            runningBalance = Math.max(0, runningBalance - principalForPeriod);

            const actualRenewalPayment = principalForPeriod + interestForPeriod;
            schedule[i].payment = actualRenewalPayment;
            schedule[i].principalPayment = principalForPeriod;
            schedule[i].interestPayment = interestForPeriod;
            schedule[i].totalPayment =
              actualRenewalPayment + schedule[i].expensePayment + (schedule[i].oneTimePayment ?? 0);
            schedule[i].remainingBalance = runningBalance;
            schedule[i].currentRate = segmentRate;
          }
        }
      }
    }
  }

  return { schedule, meta };
}

/**
 * Calculate the full amortization schedule for a mortgage.
 * Returns an array of AmortizationEntry objects, one per payment period.
 */
export function calculateSchedule(inputs: ScheduleInputs): AmortizationEntry[] {
  return calculateScheduleWithMeta(inputs).schedule;
}
